import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { Play, Users, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UnePage = () => {
  const { t, language } = useLanguage();
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Translations for founder content
  const getText = (key) => {
    const texts = {
      founders: {
        fr: 'Les Fondateurs',
        en: 'The Founders',
        uk: 'Засновники',
        ru: 'Основатели',
        it: 'I Fondatori',
        ro: 'Fondatorii',
        ka: 'დამფუძნებლები'
      },
      natalieRole: {
        fr: 'Fondatrice de la plateforme Apora',
        en: 'Founder of the Apora platform',
        uk: 'Засновниця платформи Apora',
        ru: 'Основательница платформы Apora',
        it: 'Fondatrice della piattaforma Apora',
        ro: 'Fondatoare a platformei Apora',
        ka: 'Apora პლატფორმის დამფუძნებელი'
      },
      natalieBio1: {
        fr: 'Psychologue auprès de Qualisocial, Crise UP, EutelMed, TDH Suisse en aide aux populations Ukrainiennes',
        en: 'Psychologist at Qualisocial, Crise UP, EutelMed, TDH Switzerland helping Ukrainian populations',
        uk: 'Психолог у Qualisocial, Crise UP, EutelMed, TDH Швейцарія для допомоги українському населенню',
        ru: 'Психолог в Qualisocial, Crise UP, EutelMed, TDH Швейцария для помощи украинскому населению',
        it: 'Psicologa presso Qualisocial, Crise UP, EutelMed, TDH Svizzera per aiutare le popolazioni ucraine',
        ro: 'Psiholog la Qualisocial, Crise UP, EutelMed, TDH Elveția pentru ajutorarea populației ucrainene',
        ka: 'ფსიქოლოგი Qualisocial, Crise UP, EutelMed, TDH შვეიცარიაში უკრაინული მოსახლეობის დასახმარებლად'
      },
      natalieBio2: {
        fr: 'Psychologue pour des salariés de Danone, Séphora et autres...',
        en: 'Psychologist for employees at Danone, Sephora and others...',
        uk: 'Психолог для співробітників Danone, Sephora та інших...',
        ru: 'Психолог для сотрудников Danone, Sephora и других...',
        it: 'Psicologa per i dipendenti di Danone, Sephora e altri...',
        ro: 'Psiholog pentru angajații Danone, Sephora și alții...',
        ka: 'ფსიქოლოგი Danone, Sephora და სხვა კომპანიების თანამშრომლებისთვის...'
      },
      natalieBio3: {
        fr: "Engagée dans l'aide humanitaire et le soutien à l'humain",
        en: 'Committed to humanitarian aid and human support',
        uk: 'Віддана гуманітарній допомозі та підтримці людей',
        ru: 'Преданная гуманитарной помощи и поддержке людей',
        it: "Impegnata nell'aiuto umanitario e nel supporto umano",
        ro: 'Dedicată ajutorului umanitar și sprijinului uman',
        ka: 'ერთგულია ჰუმანიტარული დახმარებისა და ადამიანური მხარდაჭერისადმი'
      },
      julienRole: {
        fr: 'Dirigeant de Vanguard Cybersecurity Consulting',
        en: 'CEO of Vanguard Cybersecurity Consulting',
        uk: 'Керівник Vanguard Cybersecurity Consulting',
        ru: 'Руководитель Vanguard Cybersecurity Consulting',
        it: 'CEO di Vanguard Cybersecurity Consulting',
        ro: 'Director al Vanguard Cybersecurity Consulting',
        ka: 'Vanguard Cybersecurity Consulting-ის ხელმძღვანელი'
      },
      julienBio1: {
        fr: 'Banques, Assurances et Industrie',
        en: 'Banking, Insurance and Industry',
        uk: 'Банки, страхування та промисловість',
        ru: 'Банки, страхование и промышленность',
        it: 'Banche, Assicurazioni e Industria',
        ro: 'Bănci, Asigurări și Industrie',
        ka: 'ბანკები, დაზღვევა და მრეწველობა'
      },
      julienBio2: {
        fr: "Engagé dans l'aide humanitaire et le soutien à l'humain",
        en: 'Committed to humanitarian aid and human support',
        uk: 'Відданий гуманітарній допомозі та підтримці людей',
        ru: 'Предан гуманитарной помощи и поддержке людей',
        it: "Impegnato nell'aiuto umanitario e nel supporto umano",
        ro: 'Dedicat ajutorului umanitar și sprijinului uman',
        ka: 'ერთგულია ჰუმანიტარული დახმარებისა და ადამიანური მხარდაჭერისადმი'
      }
    };
    return texts[key]?.[language] || texts[key]?.['fr'] || key;
  };

  useEffect(() => {
    const fetchFeaturedVideo = async () => {
      try {
        const response = await axios.get(`${API}/featured-video`);
        setFeaturedVideo(response.data);
      } catch (error) {
        console.log('No featured video available');
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedVideo();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section - Founders */}
        <section className="py-16 bg-gradient-to-br from-sage-50 to-sand-50">
          <div className="container-app">
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-center mb-12">
              {getText('founders')}
            </h1>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Text */}
              <div className="space-y-10">
                {/* Natalie */}
                <div className="bg-white rounded-2xl p-6 shadow-soft border border-sand-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Heart className="w-6 h-6 text-sage-600" />
                    </div>
                    <div>
                      <h2 className="font-heading text-xl font-semibold text-foreground mb-2">
                        Natalie Guimelli
                      </h2>
                      <p className="text-sage-600 font-medium mb-3">
                        {getText('natalieRole')}
                      </p>
                      <ul className="space-y-2 text-muted-foreground text-sm">
                        <li className="flex items-start gap-2">
                          <span className="text-sage-500 mt-1">•</span>
                          {getText('natalieBio1')}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-sage-500 mt-1">•</span>
                          {getText('natalieBio2')}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-sage-500 mt-1">•</span>
                          {getText('natalieBio3')}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Photo */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-sage-200 rounded-3xl transform rotate-3"></div>
                  <img 
                    src="/images/natalie-guimelli.jpg" 
                    alt="Natalie Guimelli"
                    className="relative rounded-3xl shadow-lg max-w-sm w-full object-cover"
                    onError={(e) => {
                      e.target.src = '/images/default-practitioner.jpg';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Video Section */}
        <section className="py-16 bg-white">
          <div className="container-app">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-center mb-4">
              {t('une.featured') || 'À la une'}
            </h2>
            <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
              {t('une.featuredSubtitle') || 'Découvrez nos praticiens à travers leurs vidéos de présentation'}
            </p>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600"></div>
              </div>
            ) : featuredVideo ? (
              <div className="max-w-4xl mx-auto">
                <div className="relative bg-black rounded-2xl overflow-hidden shadow-lg aspect-video">
                  <video 
                    src={`${process.env.REACT_APP_BACKEND_URL}${featuredVideo.url}`}
                    controls 
                    className="w-full h-full"
                    poster={featuredVideo.thumbnail}
                  >
                    Votre navigateur ne supporte pas la lecture de vidéos.
                  </video>
                </div>
                {featuredVideo.title && (
                  <div className="mt-4 text-center">
                    <h3 className="font-heading text-xl font-medium">{featuredVideo.title}</h3>
                    {featuredVideo.practitioner_name && (
                      <p className="text-muted-foreground">{featuredVideo.practitioner_name}</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-sand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="w-10 h-10 text-sand-400" />
                </div>
                <p className="text-muted-foreground">
                  {t('une.noVideo') || 'Aucune vidéo à la une pour le moment'}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-sage-600 text-white">
          <div className="container-app text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-4">
              {t('une.ctaTitle') || 'Rejoignez notre communauté'}
            </h2>
            <p className="text-sage-100 mb-8 max-w-2xl mx-auto">
              {t('une.ctaSubtitle') || 'Que vous soyez praticien ou à la recherche d\'un accompagnement, Apora vous connecte avec les meilleurs professionnels du bien-être.'}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a 
                href="/search" 
                className="inline-flex items-center gap-2 bg-white text-sage-600 px-6 py-3 rounded-xl font-medium hover:bg-sand-50 transition-colors"
              >
                <Users className="w-5 h-5" />
                {t('une.findPractitioner') || 'Trouver un praticien'}
              </a>
              <a 
                href="/auth?tab=register" 
                className="inline-flex items-center gap-2 bg-sage-700 text-white px-6 py-3 rounded-xl font-medium hover:bg-sage-800 transition-colors border border-sage-500"
              >
                {t('une.becomePractitioner') || 'Devenir praticien'}
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-white">
        <div className="container-app text-center">
          <p className="text-sm text-white/60">
            © 2025 Opora Vie. {t('landing.allRights') || 'Tous droits réservés.'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default UnePage;
